<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatDistanceToNow } from 'date-fns';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import { useCurrency } from 'dashboard/composables/useCurrency';

const props = defineProps({
  companies: { type: Array, default: () => [] },
  selectedIds: { type: Array, default: () => [] },
  activeSort: { type: String, default: 'name' },
  activeOrdering: { type: String, default: '' },
});

const emit = defineEmits([
  'showCompany',
  'toggleSelection',
  'toggleSelectAll',
  'update:sort',
]);

const { t } = useI18n();
const { currency } = useCurrency();

// Só as colunas que o backend sabe ordenar (sort_on no controller) recebem
// header clicável; as demais são apenas rótulos.
const COLUMNS = [
  {
    key: 'name',
    label: 'COMPANIES.TABLE.COMPANY',
    sortable: true,
    align: 'left',
  },
  {
    key: 'industry',
    label: 'COMPANIES.TABLE.SEGMENT',
    sortable: true,
    align: 'left',
  },
  {
    key: 'location',
    label: 'COMPANIES.TABLE.LOCATION',
    sortable: false,
    align: 'left',
  },
  {
    key: 'contacts',
    label: 'COMPANIES.TABLE.CONTACTS',
    sortable: false,
    align: 'right',
  },
  {
    key: 'pipeline',
    label: 'COMPANIES.TABLE.PIPELINE',
    sortable: false,
    align: 'right',
  },
  {
    key: 'status',
    label: 'COMPANIES.TABLE.STATUS',
    sortable: false,
    align: 'left',
  },
  {
    key: 'updated_at',
    label: 'COMPANIES.TABLE.UPDATED',
    sortable: true,
    align: 'right',
  },
];

const allSelected = computed(
  () =>
    props.companies.length > 0 &&
    props.companies.every(c => props.selectedIds.includes(c.id))
);

const someSelected = computed(
  () => props.selectedIds.length > 0 && !allSelected.value
);

const currencyFormatter = computed(
  () =>
    new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency.value,
      maximumFractionDigits: 0,
    })
);

const STATUS_STYLES = {
  customer: 'bg-n-teal-3 text-n-teal-11',
  client: 'bg-n-teal-3 text-n-teal-11',
  active: 'bg-n-teal-3 text-n-teal-11',
  prospect: 'bg-n-blue-3 text-n-blue-11',
  lead: 'bg-n-amber-3 text-n-amber-11',
  inactive: 'bg-n-alpha-2 text-n-slate-10',
};

const rows = computed(() =>
  props.companies.map(company => {
    const type = (company.companyType || company.status || '').toString();
    const key = type.toLowerCase();
    const pipeline = Number(
      company.pipelineValue || company.totalDealValue || 0
    );
    return {
      id: company.id,
      name: company.name || t('COMPANIES.UNNAMED'),
      domain: company.domain || '',
      avatarUrl: company.avatarUrl || null,
      industry: company.industry || '',
      location: [company.city, company.state].filter(Boolean).join(', '),
      contacts: company.contactsCount || 0,
      pipeline,
      pipelineLabel:
        pipeline > 0 ? currencyFormatter.value.format(pipeline) : '—',
      statusLabel: type,
      statusClass: STATUS_STYLES[key] || 'bg-n-alpha-2 text-n-slate-10',
      updatedLabel: company.updatedAt
        ? formatDistanceToNow(new Date(company.updatedAt), { addSuffix: true })
        : '',
    };
  })
);

const isSelected = id => props.selectedIds.includes(id);

const onToggleSelectAll = () => emit('toggleSelectAll', !allSelected.value);

const onToggleRow = (id, value) => emit('toggleSelection', { id, value });

const onSort = column => {
  if (!column.sortable) return;
  // 3-way não; alterna asc/desc na coluna, default asc ao trocar de coluna.
  const isActive = props.activeSort === column.key;
  const order = isActive && props.activeOrdering !== '-' ? '-' : '';
  emit('update:sort', { sort: column.key, order });
};

const sortIcon = column => {
  if (!column.sortable || props.activeSort !== column.key) return null;
  return props.activeOrdering === '-'
    ? 'i-lucide-arrow-down'
    : 'i-lucide-arrow-up';
};
</script>

<template>
  <div class="w-full overflow-x-auto">
    <table class="w-full text-sm border-collapse min-w-[720px]">
      <thead>
        <tr class="border-b border-n-weak">
          <th class="w-10 py-2.5 ltr:pl-6 rtl:pr-6">
            <Checkbox
              :model-value="allSelected"
              :indeterminate="someSelected"
              @update:model-value="onToggleSelectAll"
            />
          </th>
          <th
            v-for="column in COLUMNS"
            :key="column.key"
            class="py-2.5 px-3 text-xs font-medium tracking-wide uppercase text-n-slate-10 whitespace-nowrap select-none"
            :class="[
              column.align === 'right' ? 'text-right' : 'text-left',
              column.sortable ? 'cursor-pointer hover:text-n-slate-12' : '',
            ]"
            @click="onSort(column)"
          >
            <span
              class="inline-flex items-center gap-1"
              :class="column.align === 'right' ? 'flex-row-reverse' : ''"
            >
              {{ t(column.label) }}
              <Icon
                v-if="sortIcon(column)"
                :icon="sortIcon(column)"
                class="size-3 text-n-blue-text"
              />
            </span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="row in rows"
          :key="row.id"
          class="border-b cursor-pointer group border-n-weak/60 transition-colors hover:bg-n-alpha-1"
          :class="isSelected(row.id) ? 'bg-n-blue-2' : ''"
          @click="emit('showCompany', row.id)"
        >
          <td class="py-2.5 ltr:pl-6 rtl:pr-6" @click.stop>
            <Checkbox
              :model-value="isSelected(row.id)"
              @update:model-value="value => onToggleRow(row.id, value)"
            />
          </td>
          <td class="py-2.5 px-3">
            <div class="flex items-center min-w-0 gap-2.5">
              <Avatar
                :username="row.name"
                :src="row.avatarUrl"
                :name="row.name"
                :size="32"
                class="shrink-0"
                hide-offline-status
                rounded-full
              />
              <div class="flex flex-col min-w-0">
                <span
                  class="font-medium truncate text-n-slate-12 group-hover:text-n-blue-text"
                >
                  {{ row.name }}
                </span>
                <span
                  v-if="row.domain"
                  class="text-xs truncate text-n-slate-10"
                >
                  {{ row.domain }}
                </span>
              </div>
            </div>
          </td>
          <td class="py-2.5 px-3 text-n-slate-11 whitespace-nowrap">
            <span class="line-clamp-1">{{ row.industry || '—' }}</span>
          </td>
          <td class="py-2.5 px-3 text-n-slate-11 whitespace-nowrap">
            {{ row.location || '—' }}
          </td>
          <td
            class="py-2.5 px-3 text-right tabular-nums text-n-slate-11 whitespace-nowrap"
          >
            {{ row.contacts }}
          </td>
          <td
            class="py-2.5 px-3 text-right font-mono tabular-nums whitespace-nowrap"
            :class="row.pipeline > 0 ? 'text-n-slate-12' : 'text-n-slate-9'"
          >
            {{ row.pipelineLabel }}
          </td>
          <td class="py-2.5 px-3">
            <span
              v-if="row.statusLabel"
              class="inline-flex items-center px-2 py-0.5 text-xs font-medium capitalize rounded-full whitespace-nowrap"
              :class="row.statusClass"
            >
              {{ row.statusLabel }}
            </span>
            <span v-else class="text-n-slate-9">—</span>
          </td>
          <td
            class="py-2.5 ltr:pr-6 rtl:pl-6 px-3 text-right text-n-slate-10 whitespace-nowrap"
          >
            {{ row.updatedLabel }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
